import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, logout } from "../lib/api";
import { useMe, useOrg } from "../lib/session";
import { ErrorText } from "./ui";

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

const left = (until: string, now: number) => {
  const s = Math.max(0, Math.round((new Date(until).getTime() - now) / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : m > 0 ? `${m} min` : `${s} s`;
};

/** SupportBanner tells SVENEX support whose SVENEX they're in, and until when. */
export function SupportBanner() {
  const me = useMe();
  const now = useNow(15_000);
  const s = me.data?.support;
  if (!s) return null;
  const org = me.data?.organizations[0]?.display_name ?? "this company";
  return (
    <div className="support-banner" role="status">
      <span><b>SVENEX support</b> · {s.operator_name} in {org} as {me.data?.user.display_name || me.data?.user.email} · {left(s.ends_at, now)} left. Changes here are logged as SVENEX support.</span>
      <button className="small" onClick={() => logout().finally(() => window.location.assign("/login"))}>End access</button>
    </div>
  );
}

interface Grant { id: string; member_name: string; mine: boolean; minutes: number; status: "waiting" | "active" | "ended"; code_expires_at: string; operator_name: string | null; ends_at: string | null; ended_at: string | null; end_reason: string | null; created_at: string }
interface Overview { allowed: boolean; can_manage: boolean; grants: Grant[] }

const durations: [number, string][] = [[15, "15 minutes"], [30, "30 minutes"], [60, "1 hour"], [120, "2 hours"], [240, "4 hours"]];
const endReason: Record<string, string> = { ended_by_company: "ended by you", signed_out: "SVENEX finished", replaced: "replaced by a new code", too_many_wrong_codes: "cancelled after wrong codes", turned_off: "support access turned off" };

/**
 * SupportCodeCard lets anyone in the company give SVENEX support a one-time
 * code to use SVENEX as them (an admin's code opens the whole company), and
 * shows and ends access in progress.
 */
export function SupportCodeCard() {
  const { base, orgId, role } = useOrg();
  const me = useMe();
  const qc = useQueryClient();
  const now = useNow();
  const key = [orgId, "support"];
  const q = useQuery({ queryKey: key, queryFn: () => api<Overview>(`${base}/support`), refetchInterval: (x) => (x.state.data?.grants.some((g) => g.status !== "ended") ? 10_000 : false) });
  const [minutes, setMinutes] = useState(60);
  const [code, setCode] = useState<{ id: string; code: string; code_expires_at: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (me.data?.support) return null; // not from inside support access itself
  const d = q.data;
  if (!d) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  async function make() {
    setBusy(true); setError(null);
    try { setCode(await api(`${base}/support/codes`, { method: "POST", json: { minutes } })); refresh(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function end(id: string) {
    setBusy(true); setError(null);
    try { await api(`${base}/support/codes/${id}`, { method: "DELETE" }); if (code?.id === id) setCode(null); refresh(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  const open = d.grants.filter((g) => g.status !== "ended");
  const past = d.grants.filter((g) => g.status === "ended").slice(0, 5);
  const codeLive = code && new Date(code.code_expires_at).getTime() > now && open.some((g) => g.id === code.id && g.status === "waiting");
  return (
    <section className="card">
      <h2>Codes for SVENEX support</h2>
      {!d.allowed ? (
        <p className="muted small">Your company hasn't turned this on. {d.can_manage ? "You can, under Security." : "An admin can turn it on under Settings → Security."}</p>
      ) : (
        <>
          <p className="muted small">
            When SVENEX helps you {role === "crm_admin" ? "set up your company" : "with your work"}, you can let them use SVENEX as you for a while.
            Make a code and read it to them in person or on a call you started. Codes work once, for 10 minutes. Only give it to someone you know is from SVENEX.
          </p>
          <ErrorText error={error ?? q.error} />
          {codeLive ? (
            <div className="support-code">
              <span className="muted small">Read this to SVENEX</span>
              <b>{code!.code}</b>
              <span className="muted small">Works once · {left(code!.code_expires_at, now)} left</span>
              <button className="secondary small" disabled={busy} onClick={() => end(code!.id)}>Cancel code</button>
            </div>
          ) : (
            <div className="inline">
              <label className="inline small">They can use it for
                <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>{durations.map(([m, l]) => <option key={m} value={m}>{l}</option>)}</select>
              </label>
              <button disabled={busy} className={busy ? "is-busy" : undefined} onClick={make}>Make a code</button>
            </div>
          )}
        </>
      )}
      {open.filter((g) => g.status === "active").map((g) => (
        <div key={g.id} className="notice warn small support-live">
          <span><b>{g.operator_name ?? "SVENEX"}</b> is using SVENEX as {g.mine ? "you" : g.member_name} · {g.ends_at ? left(g.ends_at, now) : ""} left</span>
          <button className="danger small" disabled={busy} onClick={() => end(g.id)}>End now</button>
        </div>
      ))}
      {past.length > 0 && (
        <ul className="list small support-past">
          {past.map((g) => (
            <li key={g.id}>
              {g.operator_name ? `${g.operator_name} as ${g.mine ? "you" : g.member_name}` : `Code by ${g.mine ? "you" : g.member_name}, not used`}
              <span className="muted"> · {new Date(g.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}{g.end_reason ? ` · ${endReason[g.end_reason] ?? g.end_reason}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
